import { ref } from 'vue'

export interface DebugOptions {
  enabled?: boolean
  prefix?: string
  showTimestamp?: boolean
  logLevel?: 'debug' | 'info' | 'warn' | 'error',
  name?: string
}

export const useDebugLogger = (moduleName: string, options: DebugOptions = {}) => {
  const {
    enabled = process.env.NODE_ENV === 'development',
    prefix = '',
    showTimestamp = true,
    logLevel = 'debug',
  } = options

  const isEnabled = ref(enabled)

  // 检查是否应该输出日志
  const shouldLog = (level: string): boolean => {
    if (!isEnabled.value) return false
    
    const levels = ['debug', 'info', 'warn', 'error']
    const currentLevelIndex = levels.indexOf(logLevel)
    const messageLevelIndex = levels.indexOf(level)
    
    return messageLevelIndex >= currentLevelIndex
  }

  // 格式化消息
  const formatMessage = (level: string, ...args: any[]): string => {
    const timestamp = showTimestamp ? `[${new Date().toISOString()}] ` : ''
    const modulePrefix = moduleName ? `[${moduleName}] ` : ''
    const levelPrefix = `[${level.toUpperCase()}] `
    const customPrefix = prefix ? `[${prefix}] ` : ''
    
    return `${timestamp}${modulePrefix}${levelPrefix}${customPrefix}`
  }

  // 日志方法
  const log = (...args: any[]) => {
    if (shouldLog('debug')) {
      console.log(formatMessage('debug'), ...args)
    }
  }

  const info = (...args: any[]) => {
    if (shouldLog('info')) {
      console.info(formatMessage('info'), ...args)
    }
  }

  const warn = (...args: any[]) => {
    if (shouldLog('warn')) {
      console.warn(formatMessage('warn'), ...args)
    }
  }

  const error = (...args: any[]) => {
    if (shouldLog('error')) {
      console.error(formatMessage('error'), ...args)
    }
  }

  // 分组日志
  const group = (label: string, collapsed: boolean = false) => {
    if (!isEnabled.value) return { end: () => {} }
    
    if (collapsed) {
      console.groupCollapsed(label)
    } else {
      console.group(label)
    }
    
    return {
      end: () => console.groupEnd()
    }
  }

  // 性能测量
  const time = (label: string) => {
    if (!isEnabled.value) return { end: () => 0 }
    
    console.time(label)
    const startTime = performance.now()
    
    return {
      end: () => {
        console.timeEnd(label)
        return performance.now() - startTime
      }
    }
  }

  // 启用/禁用调试
  const enable = () => {
    isEnabled.value = true
    console.log(`[Debug] ${moduleName} 调试已启用`)
  }

  const disable = () => {
    isEnabled.value = false
    console.log(`[Debug] ${moduleName} 调试已禁用`)
  }

  // 条件调试（仅在特定条件下输出）
  const conditional = (condition: boolean, ...args: any[]) => {
    if (condition && isEnabled.value) {
      console.log(formatMessage('debug'), ...args)
    }
  }

  return {
    log,
    info,
    warn,
    error,
    group,
    time,
    enable,
    disable,
    conditional,
    isEnabled: isEnabled.value
  }
}

// 全局调试控制
export const debug = {
  enableAll: () => {
    localStorage.setItem('debug', 'true')
    console.log('[Debug] 全局调试已启用')
  },
  
  disableAll: () => {
    localStorage.removeItem('debug')
    console.log('[Debug] 全局调试已禁用')
  },
  
  isEnabled: () => {
    return process.env.NODE_ENV === 'development' || localStorage.getItem('debug') === 'true'
  }
}